import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { goToFormBoundMethodParameter } from "../interfaces";

export interface RecalculateEventArg
{
    ShowLoading: boolean;
    Recalculating: boolean;
    Message?: string;
}

export interface TypeAheadChangeEventArg
{
    ControlId: string;
    Index: number;
}

export interface BreadcrumbArg
{
    Field: string;
    Value: any;
}

@Injectable({ providedIn: "root" })
export class MetadataEventService
{
    private readonly recalculateWorking$ = new Subject<RecalculateEventArg>();

    get onRecalculateWorking(): Observable<RecalculateEventArg>
    {
        return this.recalculateWorking$.asObservable();
    }

    private readonly reinitializeView$ = new Subject<void>();

    get onReinitializeView(): Observable<void>
    {
        return this.reinitializeView$.asObservable();
    }

    fireRecalculateWorkingListeners(value: RecalculateEventArg, reInit: boolean): void
    {
        this.recalculateWorking$.next(value);
        if (reInit)
            this.reinitializeView$.next();
    }

    private readonly loadCardMetadata$ = new Subject<void>();

    get onLoadCardMetadata(): Observable<void>
    {
        return this.loadCardMetadata$.asObservable();
    }

    fireLoadCardMetadataListeners(): void
    {
        this.loadCardMetadata$.next();
    }

    private readonly navigateView$ = new Subject<string>();

    get onNavigateView(): Observable<string>
    {
        return this.navigateView$.asObservable();
    }

    fireNavigateViewListeners(value: string): void
    {
        this.navigateView$.next(value);
    }

    private readonly navigateTabbedView$ = new Subject<string>();

    get onNavigateTabbedView(): Observable<string>
    {
        return this.navigateTabbedView$.asObservable();
    }

    fireNavigateTabbedViewListeners(value: string): void
    {
        this.navigateTabbedView$.next(value);
    }

    private readonly saveAfterRecalculate$ = new Subject<void>();

    get onSaveAfterRecalculate(): Observable<void>
    {
        return this.saveAfterRecalculate$.asObservable();
    }

    fireSaveAfterRecalculateListener(): void
    {
        this.saveAfterRecalculate$.next();
    }

    private readonly metadataRefresh$ = new Subject<void>();

    get onMetadataRefresh(): Observable<void>
    {
        return this.metadataRefresh$.asObservable();
    }

    fireMetadataRefreshListeners(): void
    {
        this.metadataRefresh$.next();
    }

    private readonly formReload$ = new Subject<void>();

    get onFormReload(): Observable<void>
    {
        return this.formReload$.asObservable();
    }

    fireFormReloadListeners(): void
    {
        this.formReload$.next();
    }

    private readonly searchFormBreadcrumb$ = new Subject<BreadcrumbArg>();

    get onSearchFormBreadcrumb(): Observable<BreadcrumbArg>
    {
        return this.searchFormBreadcrumb$.asObservable();
    }

    fireSearchFormBreadcrumbListener(arg: BreadcrumbArg): void
    {
        this.searchFormBreadcrumb$.next(arg);
    }

    private readonly goToForm$ = new Subject<goToFormBoundMethodParameter>();

    get onGoToForm(): Observable<goToFormBoundMethodParameter>
    {
        return this.goToForm$.asObservable();
    }

    fireGoToFormListener(params: goToFormBoundMethodParameter): void
    {
        this.goToForm$.next(params);
    }

    private readonly lookupTypeCacheClear$ = new Subject<string>();

    get onLookupTypeCacheClear(): Observable<string>
    {
        return this.lookupTypeCacheClear$.asObservable();
    }

    fireLookupTypeCacheClearListener(type: string): void
    {
        this.lookupTypeCacheClear$.next(type);
    }

    private readonly loadViewCompleted$ = new Subject<boolean>();

    get onloadViewCompleted(): Observable<boolean>
    {
        return this.loadViewCompleted$.asObservable();
    }

    fireLoadViewCompletedListener(viewId: string): void
    {
        if (!viewId)
            return;
        if (this.viewLoadsFired.has(viewId))
            this.loadViewCompleted$.next(false);
        else
        {
            this.loadViewCompleted$.next(true);
            this.viewLoadsFired.add(viewId);
        }
    }

    clearViewLoads(): void
    {
        this.viewLoadsFired = new Set<string>();
    }

    private viewLoadsFired = new Set<string>();
}

